'use client'

/* eslint-disable react/jsx-no-useless-fragment */
import useSWR from 'swr'
import { ReactNode, createContext, useContext, useMemo } from 'react'
import { useSession } from 'next-auth/react'
import { aceClient } from '@/lib/utils/client'

const FeatureToggleMapContext = createContext<Map<string, boolean>>(new Map())

export type FeatureToggleProviderProps = {
  children: ReactNode
  [key: string]: boolean | ReactNode
}

export function FeatureToggleProvider({ children, ...fallbackData }: FeatureToggleProviderProps) {
  const session = useSession()
  const fallbackFeatureToggles = Object.entries(fallbackData)
    .filter((pair): pair is [string, boolean] => typeof pair[1] === 'boolean')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<string, boolean>)

  const url = `/api/feature-toggles` as const
  const featureToggles = useSWR(
    session.data !== null ? url : null,
    () => aceClient.get<Record<string, boolean>>(url).then((response) => response.data),
    {
      fallbackData: fallbackFeatureToggles,
      refreshInterval: 60 * 1_000,
    }
  )
  return (
    <FeatureToggleMapContext.Provider
      value={useMemo(() => new Map(Object.entries(featureToggles.data)), [featureToggles.data])}
    >
      {children}
    </FeatureToggleMapContext.Provider>
  )
}

export function useFeatureToggle(key: string): boolean {
  const featureToggleMap = useContext(FeatureToggleMapContext)
  return featureToggleMap.get(key) ?? false
}

export type FeatureToggleProps = {
  feature: string
  on?: ReactNode
  off?: ReactNode
}

export function FeatureToggle({ on, off, feature }: FeatureToggleProps) {
  const isEnabled = useFeatureToggle(feature)
  return <>{isEnabled ? on : off}</>
}
